import { redirect } from '@remix-run/node';
import crypto from 'crypto';

/**
 * 将 url 转为 https 协议
 * @param url 转换的 URL 地址
 * @returns {string}
 */
export function convertHttps(url: string) {
  if (url.startsWith('http://')) {
    return url.replace('http', 'https');
  }
  if (url.startsWith('//')) {
    return `https:${url}`;
  }
  return url;
}

/**
 * 将key字符串转换成下划线方式命名 (如 "some_name") 的字符串
 * @param key 对象字符串
 * @param ignoreFirst 是否忽略第一个大写字母，如果忽略，会将其当成小写字母处理
 */
export function underlizeKey(key: string, ignoreFirst = false): string {
  const out: string[] = [];
  let i = 0;
  const lowerCasedStr: string = key.toString().toLowerCase();
  while (i < key.length) {
    if (key[i] !== lowerCasedStr[i]) {
      if (!ignoreFirst || i !== 0) {
        out.push('_');
        out.push(lowerCasedStr[i]);
        i++;
        continue;
      }
    }
    out.push(key[i].toLocaleLowerCase());
    i++;
  }
  return out.join('');
}

/**
 * 将对象键值对中的 key 转换为按照下划线方式命名的 key
 * @param obj 需要转换的对象
 */
export function underlize(obj: object | null): object | null | undefined {
  if (obj === null) {
    return null;
  }
  if (obj === undefined) {
    return undefined;
  }
  if (Array.isArray(obj)) {
    return obj.map(item => underlize(item));
  }
  if (typeof obj === 'object') {
    const out: any = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
      const v = (obj as any)[key];
      out[underlizeKey(key)] = underlize(v);
    }
    return out;
  }
  return obj;
}

/**
 * 将key字符串转换成驼峰方式命名（如 "someName"） 的字符串
 * @param key string类型
 * @param separators key分隔符 "-"中划线/"_"下划线
 */
export function camelizeKey(
  key: string,
  separators: string[] = ['-', '_']
): string {
  const out: any = [];
  let i = 0;
  const separatorsSet = new Set(separators);
  while (i < key.length) {
    if (separatorsSet.has(key[i])) {
      out.push(key[i + 1].toUpperCase());
      i++;
    } else {
      out.push(key[i]);
    }
    i++;
  }
  return out.join('');
}

/**
 * 将对象键值对中的 key 转换为按照驼峰方式命名的 key
 * @param obj
 */
export function camelize(obj: object): { [key: string]: any } | null {
  if (obj === null || obj === undefined) {
    return null;
  }
  if (Array.isArray(obj)) {
    return obj.map(item => camelize(item));
  }
  if (typeof obj === 'object') {
    const out: any = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
      const v = (obj as any)[key];
      out[camelizeKey(key)] = camelize(v);
    }
    return out;
  }
  return obj;
}

/**
 * 获得URL中”？“后面的参数 以键值对的形式放回
 * @param url string类型
 */
export function getUrlArgs<T extends Record<string, any>>(
  url: string,
  isCamelize = false
): T {
  // remove url hash;
  // eslint-disable-next-line prefer-destructuring
  let s = url.split('?')[1];
  if (!s) {
    return {} as unknown as T;
  }
  if (s.includes('#')) {
    // eslint-disable-next-line prefer-destructuring
    s = s.split('#')[0];
  }
  let args: T = {} as any;
  // eslint-disable-next-line no-restricted-syntax
  for (const a of s.split('&')) {
    const part = a.split('=') as (keyof T)[];
    let str: keyof T = '';
    try {
      str = decodeURIComponent(part[1] as string);
    } catch (error) {
      // eslint-disable-next-line prefer-destructuring
      str = part[1];
    }
    args[part[0]] = str as any;
  }
  if (isCamelize) {
    args = camelize(args) as T;
  }
  return args as unknown as T;
}

export function isEmptyObj(obj: { [key: string]: any } | null) {
  if (!obj) {
    return true;
  }
  if (Object.keys(obj).length === 0) {
    return true;
  }
  return false;
}

export function createObject<T extends { [key: string]: any }>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * 向给定 url 后添加 query 参数
 * @param url url
 * @param args 参数
 */
export function addUrlArgs(
  url: string,
  args: { [key: string]: string | number },
  useEncode = true
): string {
  const res = url.split('#');
  // eslint-disable-next-line prefer-destructuring
  let baseUrl = res[0];
  // eslint-disable-next-line prefer-destructuring
  const hash = res[1];
  const parts: string[] = [];
  const keys = Object.keys(args || {}).sort();
  // eslint-disable-next-line no-restricted-syntax
  for (const k of keys) {
    const v = args[k];
    if (v !== undefined && v !== null) {
      if (useEncode) {
        parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
      } else {
        parts.push(`${k}=${v}`);
      }
    }
  }
  if (baseUrl.includes('?')) {
    baseUrl += `&${parts.join('&')}`;
  } else {
    baseUrl += `?${parts.join('&')}`;
  }
  if (hash) {
    return `${baseUrl}#${hash}`;
  }
  return baseUrl;
}

/**
 * encode 字符串， 防止重复 encode
 * @param v 需要 encode 的 value
 * @returns {string} 返回 encode 之后的值
 */
export function encode(v: string) {
  if (decodeURIComponent(v) === v) {
    return encodeURIComponent(v);
  }
  return v;
}

/**
 * 克隆给定对象
 * @param target 源对象
 * @param deepClone 是否进行深拷贝，如果为 false，只进行一层拷贝，深层都为引用
 */
export function clone<T>(target: T, deepClone = false): T {
  if (Array.isArray(target)) {
    const res = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const item of target) {
      if (deepClone) {
        res.push(clone(item, deepClone));
      } else {
        res.push(item);
      }
    }
    return res as any;
  }
  if (typeof target === 'object') {
    const res = {} as any;
    // eslint-disable-next-line no-restricted-syntax
    for (const key in target) {
      if (deepClone) {
        res[key] = clone(target[key], deepClone);
      } else {
        res[key] = target[key];
      }
    }
    return res;
  }
  return target;
}

/**
 * 判断目标值是否是对象 Object
 * @param target 目标值
 * @returns
 */
export function isPaintObject(target: any) {
  return Object.prototype.toString.call(target) === '[Object Object]';
}

/**
 * 判断目标值是否是对象 数组
 * @param target 目标值
 * @returns
 */
export function isArray(target: any) {
  return Object.prototype.toString.call(target) === '[object Array]';
}

/**
 * 判断目标值是否是  Number
 * @param target 目标值
 * @returns
 */
export function isNumber(target: any) {
  return Object.prototype.toString.call(target) === '[object Number]';
}

/**
 * 判断目标值是否是  String
 * @param target 目标值
 * @returns
 */
export function isString(target: any) {
  return Object.prototype.toString.call(target) === '[object String]';
}

/**
 * 判断目标值是否是  布尔值
 * @param target 目标值
 * @returns
 */
export function isBoolean(target: any) {
  return Object.prototype.toString.call(target) === '[object Boolean]';
}

/**
 * 加密手机号中间的号码
 * @param phone 手机号字符串
 * @returns
 */
export function encodePhone(phone: string): string {
  if (!phone) {
    return '';
  }
  return phone.replace(/^(\d{3})\d{4}(\d+)/, '$1****$2');
}

/**
 * 加密用户名称
 * @param name 用户名称
 * @returns
 */
export function encodeRealName(name: string): string {
  if (!name) {
    return '';
  }
  const reg = /(?<=.)./g;
  return name.replace(reg, '*');
}

/**
 * 获取 min, max 之间的随机数
 * @param max 最大值
 * @param min 最小值
 * @returns {number}
 */
export function random(max: number, min: number) {
  return Math.round(Math.random() * (max - min) + min);
}

/**
 * 随机生成固定长度的 hash 字符串
 * @param length 随机字符串的长度
 * @returns
 */
export function randomString(length = 5) {
  let result = '';
  const characters =    'ABCDEFGHIJKLMNOPQRSTUVMWXYZabDScdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

/**
 * 在 count 的基础上 fake 出一个新的数
 * @param count 基础数据
 * @returns
 */
export function fakeCount(count: number) {
  if (count <= 0 || (count > 0 && count <= 20)) {
    return count;
  }

  if (count >= 1000) {
    return count;
  }

  const c = count * 11 + 2;

  return c;
}


/**
 * 使用 FileReader 读取原始文件
 * @param file 原始文件对象
 * @returns 返回base64 的文件
 */
export const asyncFileReader = (file: File) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  return new Promise((ok) => {
    reader.onload = (e) => {
      ok(e.target?.result as string);
    };
  }) as Promise<string>;
};

/*
 * 加密敏感字符串
 * 手机号tel
 * 邮箱email
 * */
export function hashStr(str: string, hashLen = 7, prefixLen = 3) {
  if (!str) {
    return '';
  }
  if (str.length > hashLen) {
    const len = str.length - hashLen;
    const hash = new Array(len).fill('*')
      .join('');
    return `${str.slice(0, prefixLen)}${hash}${str.slice(prefixLen + len)}`;
  }
  const hash = new Array(str.length - prefixLen).fill('*')
    .join('');
  return `${str.slice(0, prefixLen)}${hash}`;
}

/**
 * hash 加密手机号
 * @param tel 手机号
 * @returns
 */
export function hashTel(tel: string) {
  if (!tel) {
    return '';
  }
  return hashStr(tel);
}

/**
 * hash 加密邮箱
 * @param email 用户 email
 * @returns
 */
export function hashEmail(email: string) {
  if (!email) {
    return '';
  }
  const [prefix, suffix] = email.split('@');
  const hashPrefix = hashStr(prefix, 7, 2);
  return `${hashPrefix}@${suffix}`;
}

/**
 * 当前是否是微信浏览器环境
 * @returns {bool}
 */
export function isWechatBrowser() {
  return /(micromessenger|webbrowser)/.test(navigator.userAgent.toLocaleLowerCase());
}

/**
 * 当前是否是安卓环境
 * @returns {bool}
 */
export function isAndroid() {
  const u = navigator.userAgent;
  return u.indexOf('Android') > -1 || u.indexOf('Adr') > -1;
}

/**
 * 将 searchParams 对象转换为 Object key-value 对象
 * @param searchParams searchParams 对象
 * @returns {bool}
 */
export function searchParamsToObject(searchParams: URLSearchParams) {
  const result: { [key: string]: any } = {};
  for (const [key, value] of searchParams.entries()) {
    if (value !== 'undefined') {
      result[key] = value;
    }
  }
  return result;
}

/**
 * 截取字符串末尾长度
 * @param str 字符串
 * @param len 截取的长度
 * @returns
 */
export function sliceStringLast(str: string, len: number) {
  if (len >= str.length) {
    return str;
  }
  return str.substring(str.length - len);
}

/**
 * 替换原先的 entries 函数， 让其有 ts 类型推导
 * @param o 目标对象
 * @returns
 */
export function entries<T extends Object>(o: T) {
  return Object.entries(o) as unknown as [keyof T, T[keyof T]][];
}

/**
 * 获取 grp 评审系统 open api 的签名校验 header 头
 * @returns {Record<string, string>} headers
 */
export function getReviewSystemApiSignatureHeaders() {
  const destination = 'grp_portal_data';
  const source = 'grp_office_portal_data';
  const nonce = 'au8X6q';
  const key = '9e593ff5-7b00-11ea-9f2b-7ea78ffae334';
  const timestamp = Math.round(new Date().getTime() / 1000);
  const hashInstance = crypto.createHash('md5');
  const value = hashInstance
    .update(`${destination},${source},${timestamp},${nonce},${key}`)
    .digest('hex');
  const sign = value.toLowerCase();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Odp-Destination-Service': 'grp_portal_data',
    'X-Odp-Source-Service': 'grp_office_portal_data',
    'X-Odp-Timestamp': `${timestamp}`,
    'X-Odp-Nonce': 'au8X6q',
    'X-Odp-Signature': sign,
  };
  return headers;
}

/*
 * 双端、海外和国内版自动跳转
 * */
export default function getPlatform(clientInfo: Headers) {
  // const lang = clientInfo.get('accept-language');
  let platForm = 'pc';
  if (
    clientInfo
      .get('user-agent')
      ?.match(/(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i)
  ) {
    platForm = 'mobile';
  }

  return platForm;
}

// 获取浏览器类型
export function getBrowserEnv(userAgent: string) {
  if (!userAgent) {
    return '';
  }
  if (/(micromessenger|webbrowser)/.test(userAgent.toLocaleLowerCase())) {
    return 'weixin';
  }
  return 'other';
}

// 获取手机系统类型
export function getPhoneOs(userAgent: string) {
  if (!userAgent) {
    return '';
  }
  if (userAgent.indexOf('Android') > -1 || userAgent.indexOf('Adr') > -1) {
    return 'android';
  }
  return 'ios';
}

/**
 * 当前环境是否是浏览器环境
 */
export const isBrowser = typeof window !== 'undefined';


// 获取用户当前的浏览器环境
export function getUserEnv(userAgent: string) {
  const browserEnv = getBrowserEnv(userAgent);
  const os = getPhoneOs(userAgent);

  return { browserEnv, os };
}

// 获取环境变量
export function getEnvValue(key: string) {
  if (isBrowser) {
    return window.ENV[key];
  }
  return process.env[key];
}

// 根据用户角色获取权限等级
export function getRoleLevel(role: Role) {
  switch (role) {
    case 'superAdmin':
      return 1000;
    case 'admin':
      return 100;
    case 'normal':
      return 1;
    case 'operation':
      return 10;
    default:
      return 1;
  }
}

export const noAuthPage = (req: Request) => {
  if (req.url.includes('403')) {
    return {} as any;
  }
  return redirect('/403');
};


export function isEmailString(email: string) {
  const re = /\S+@\S+\.\S+/;
  return re.test(email);
}


export function convertFormDataToObject<T extends Record<string, string>>(formData: FormData) {
  const obj: T = {} as unknown as T;
  formData.forEach((value, key) => {
    (obj as any)[key] = value;
  });
  return obj as T;
}

/*
  链接复制
*/
export async function copyCommand(text: string) {
  const execCommandCopy = (text: string) => {
    const inputEl = document.createElement('input');

    inputEl.style.position = 'fixed';

    inputEl.style.bottom = '-100000px';

    inputEl.setAttribute('readonly', 'readonly');

    inputEl.value = text;

    document.body.appendChild(inputEl);

    inputEl.select();

    document.execCommand('Copy');

    inputEl.remove();

    alert(`Copied successfully:${text}`);
  };
  try {
    if (navigator.clipboard && navigator.permissions) {
      await window.navigator.clipboard.writeText(text);
      console.log(`Copied successfully: ${text}`);
      alert(`Copied successfully:${text}`);
    } else {
      execCommandCopy(text);
    }
  } catch (err) {
    console.error('Unable to copy text: ', err);
    execCommandCopy(text);
  }
}
